// Shape modeled from the Quotes service contract described in
// docs/quote-management-navigation-plan.md §4 (QuoteDetails / QuoteApprovalRequest) -
// not yet confirmed against a real GET /quotes response the way IShipment was
// (see shipment.interface.ts). Only fields the grid + approval flows
// actually consume are typed here.

// QuoteStatus enum values per plan §4. No `Rejected` member exists - the
// `Rejected` tab reads `Declined` (Revision 2 decision, plan §5/§6).
export enum QuoteStatus {
  Unknown = 0,
  Created = 1,
  Posted = 2,
  ReviewBids = 3,
  BiddingClosed = 4,
  SubmittedForApproval = 5,
  Approved = 6,
  Declined = 7,
  Cancelled = 8
}

// ApprovalStatus lives one level down, on Approver entries nested inside
// CustomerApproval/InternalApproval - not a top-level QuoteStatus (plan §4).
export enum ApprovalStatus {
  Unknown = 0,
  Draft = 1,
  Sent = 2,
  Approved = 3,
  Rejected = 4,
  Cancelled = 3,
}

export interface IQuoteDateWindow {
  readyDate: string;
  closeDate?: string;
}

export interface IQuoteApprover {
  approverId: string;
  status: ApprovalStatus;
  reason?: string;
}

export interface IQuoteApproval {
  approvers?: IQuoteApprover[];
}

// Confirmed against a real GET /quotes response: the field is
// `handlingUnitRequests` (not `handlingUnits`), and each entry carries a
// `handlingUnitTemplateId` that joins into GetQuoteSettings'
// HandlingUnitTypeDetails for a display name (see
// useQuoteSettings.ts/quote-grid.columns.tsx's HuProduct column). `weight`
// keeps the same shape Shipments' IShipmentHandlingUnit uses - display unit
// still comes off each unit's own `weight.Unit` rather than a separate
// tenant UOM lookup.
export interface IQuoteHandlingUnit {
  id: number;
  handlingUnitTemplateId?: string;
  quantity: number;
  weight: {
    Unit: string;
    Value: number;
  };
}

// TBD - GetQuoteEndpoint's real charges/cost shape isn't confirmed (plan §4
// only confirms `QuoteDetailsUpdateById` accepts a "cost" on update, not its
// read shape). Modeled as a flat total for now rather than guessing at a
// breakdown; flagged the same way shipment-grid.columns.tsx flags TransportID.
export interface IQuoteCost {
  totalCost?: number;
  currency?: string;
}

// `id` (not `quoteId`) and `handlingUnitRequests` (not `handlingUnits`) -
// confirmed against a real GET /quotes response, which has no top-level
// `quoteId` field at all.
export interface IQuote {
  id: string;
  quoteNumber: string;
  accountId?: string;
  originId: string;
  destinationId: string;
  transportationModeId: number | null;
  status: QuoteStatus;
  pickup?: IQuoteDateWindow;
  delivery?: IQuoteDateWindow;
  handlingUnitRequests?: Record<string, IQuoteHandlingUnit>;
  cost?: IQuoteCost;
  customerApproval?: IQuoteApproval;
  internalApproval?: IQuoteApproval;
}
